import React, { useState } from 'react';
import { Icon } from '../../design/Icon';
import { Badge } from '../../components/shared/Badges';
import { Dialog, MenuItem, Popover, useToast } from '../../components/shared/Overlay';
import { ErrorState } from '../../components/shared/States';
import { errorMessage, reportsAPI } from '../../services/reportsApi';
import { useAccountTimeZone } from '../../layout/ShellStatus';
import { describeCron, lastRunBadge, nextRunText, recipientsText, sortSchedules } from './reportsLogic';

const FORMAT_LABEL = { Pdf: 'PDF', Xlsx: '.xlsx', Csv: '.csv' };

function KillSwitch({ delivery }) {
  if (!delivery) return null;
  if (delivery.emailEnabled) {
    return (
      <div className="kill" role="status" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
        <span className="ki" style={{ color: 'var(--good)' }}>
          <Icon name="mail" />
        </span>
        <div className="grow">
          <div className="strong">Email delivery is on</div>
          <div className="small sub">Scheduled reports are emailed to their recipients. Only a Vertolytics super administrator can turn delivery off.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="kill" role="status">
      <span className="ki">
        <Icon name="power" />
      </span>
      <div className="grow">
        <div className="strong">Email delivery is off</div>
        <div className="small sub">
          Email delivery is turned off for this environment. Scheduled reports are still created but not sent. Only a Vertolytics super administrator can turn delivery on.
        </div>
      </div>
      <label className="switch" title="Locked by super administrator">
        <input type="checkbox" disabled aria-label="Email delivery" /> <Icon name="lock" className="ico-sm" />
      </label>
    </div>
  );
}

export function SchedulesTab({ schedules, directory, error, delivery, onNew, onEdit, onChanged }) {
  const toast = useToast();
  const { label: zoneLabel } = useAccountTimeZone();
  const [menu, setMenu] = useState(null);
  const [busy, setBusy] = useState(null);
  const [deleting, setDeleting] = useState(null);

  if (error) {
    const forbidden = error.response?.status === 403;
    return (
      <ErrorState
        title={forbidden ? 'Schedules need the Manage reports permission' : "Schedules couldn't load"}
        body={forbidden ? 'Ask an account administrator if you need to create or change scheduled reports.' : 'The reporting service didn’t respond. Scheduled reports are unaffected and will still render on time.'}
        error={forbidden ? null : error}
        onRetry={forbidden ? undefined : onChanged}
      />
    );
  }

  const runNow = async (schedule) => {
    setBusy(schedule.id);
    try {
      const result = await reportsAPI.runScheduleNow(schedule.id);
      const sent = result.outcome === 'Delivered';
      toast(
        result.outcome === 'Failed' ? result.message || `“${schedule.name}” failed` : sent ? `Sent “${schedule.name}” to ${result.recipientCount} recipients` : `Rendered ${result.fileName || schedule.name}; not sent while email delivery is off`,
        result.outcome === 'Failed' ? 'alert' : 'checkCircle'
      );
    } catch (e) {
      toast(await errorMessage(e, `“${schedule.name}” couldn't run`), 'alert');
    } finally {
      setBusy(null);
      onChanged();
    }
  };

  const setEnabled = async (schedule, enabled) => {
    setMenu(null);
    try {
      await reportsAPI.updateSchedule(schedule.id, { ...schedule, enabled });
      toast(enabled ? `Turned on “${schedule.name}”` : `Paused “${schedule.name}”`);
      onChanged();
    } catch (e) {
      toast(await errorMessage(e, "The schedule couldn't be changed"), 'alert');
    }
  };

  const remove = async (schedule) => {
    setDeleting(null);
    try {
      await reportsAPI.deleteSchedule(schedule.id);
      toast(`Deleted “${schedule.name}”`);
      onChanged();
    } catch (e) {
      toast(await errorMessage(e, "The schedule couldn't be deleted"), 'alert');
    }
  };

  return (
    <div className="stack-lg">
      <KillSwitch delivery={delivery} />
      <section className={['card', schedules ? '' : 'is-loading'].filter(Boolean).join(' ')}>
        <div className="card-head">
          <div>
            <div className="t">Scheduled reports</div>
            <div className="d">Times are {zoneLabel}</div>
          </div>
          <div className="acts">
            <button type="button" className="btn btn-primary btn-sm" onClick={onNew}>
              <Icon name="plus" className="ico-sm" />
              New schedule
            </button>
          </div>
        </div>
        <div className="card-body flush">
          {schedules && schedules.length === 0 ? (
            <p className="caption" style={{ padding: '12px 16px', margin: 0 }}>
              No schedules yet. Save a report, then schedule it to render on a fixed day and time.
            </p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Report</th>
                    <th>Runs</th>
                    <th>Format</th>
                    <th>Recipients</th>
                    <th>Next run</th>
                    <th>Last run</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sortSchedules(schedules).map((schedule) => {
                    const badge = lastRunBadge(schedule);
                    return (
                      <tr key={schedule.id}>
                        <td className="cell-title">{schedule.name}</td>
                        <td className="small">{describeCron(schedule.cronExpression)}</td>
                        <td>
                          <Badge>{FORMAT_LABEL[schedule.format] || schedule.format}</Badge>
                        </td>
                        <td className="small">{recipientsText(schedule, directory)}</td>
                        <td className="small nowrap">{schedule.enabled ? nextRunText(schedule.nextRunAt) : 'Paused'}</td>
                        <td>
                          <span title={schedule.lastRunMessage || undefined}>
                            <Badge tone={badge.tone} icon={badge.icon}>
                              {badge.text}
                            </Badge>
                          </span>
                          {schedule.lastRunOutcome === 'Failed' && schedule.lastRunMessage && <div className="caption mt-2">{schedule.lastRunMessage}</div>}
                        </td>
                        <td className="nowrap">
                          <button type="button" className="btn btn-sm btn-ghost" disabled={busy === schedule.id} onClick={() => runNow(schedule)}>
                            Run now
                          </button>
                          <Popover
                            open={menu === schedule.id}
                            onClose={() => setMenu(null)}
                            align="right"
                            width={200}
                            label={`${schedule.name} actions`}
                            anchor={
                              <button type="button" className="btn btn-sm btn-ghost icon-btn" aria-label="More" onClick={() => setMenu(menu === schedule.id ? null : schedule.id)}>
                                <Icon name="more" />
                              </button>
                            }
                          >
                            <MenuItem
                              icon="edit"
                              onClick={() => {
                                setMenu(null);
                                onEdit(schedule);
                              }}
                            >
                              Edit schedule
                            </MenuItem>
                            <MenuItem icon={schedule.enabled ? 'pause' : 'play'} onClick={() => setEnabled(schedule, !schedule.enabled)}>
                              {schedule.enabled ? 'Pause' : 'Turn on'}
                            </MenuItem>
                            <MenuItem
                              icon="trash"
                              onClick={() => {
                                setMenu(null);
                                setDeleting(schedule);
                              }}
                            >
                              Delete schedule
                            </MenuItem>
                          </Popover>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      <Dialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="Delete schedule"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={() => remove(deleting)}>
              Delete schedule
            </button>
          </>
        }
      >
        <p className="small">Delete “{deleting?.name}”? It stops running; the saved report stays.</p>
      </Dialog>
    </div>
  );
}
